/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { z } from "zod";
import { AdminLayout } from "@/components/admin/AdminLayout";
import { OverviewTab } from "@/components/admin/traffic/OverviewTab";
import { CustomersTab } from "@/components/admin/traffic/CustomersTab";
import { FunnelTab } from "@/components/admin/traffic/FunnelTab";
import { SessionsTab } from "@/components/admin/traffic/SessionsTab";
import { ErrorsTab } from "@/components/admin/traffic/ErrorsTab";
import { Activity, Users, Filter, ListOrdered, AlertTriangle } from "lucide-react";

type Range = "24h" | "7d" | "30d";
type Tab = "overview" | "customers" | "funnel" | "sessions" | "errors";

const TABS: Array<{ key: Tab; label: string; icon: any }> = [
  { key: "overview", label: "Преглед", icon: Activity },
  { key: "customers", label: "Клиенти", icon: Users },
  { key: "funnel", label: "Фуния", icon: Filter },
  { key: "sessions", label: "Сесии", icon: ListOrdered },
  { key: "errors", label: "Грешки", icon: AlertTriangle },
];

const RANGES: Array<{ key: Range; label: string }> = [
  { key: "24h", label: "24 часа" },
  { key: "7d", label: "7 дни" },
  { key: "30d", label: "30 дни" },
];

function TrafficPage() {
  const search = Route.useSearch();
  const [tab, setTab] = useState<Tab>((search.tab as Tab) ?? "overview");
  const [range, setRange] = useState<Range>("7d");
  const [openSession, setOpenSession] = useState<string | null>(search.session ?? null);
  const [selectedUser, setSelectedUser] = useState<string | null>(search.user ?? null);

  const openSessionFromAnywhere = (id: string) => {
    setOpenSession(id);
    setTab("sessions");
  };

  return (
    <AdminLayout>
      <div className="p-4 md:p-6 space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-[20px] font-black text-slate-900">Трафик</h1>
            <p className="text-[12px] text-slate-500">
              Кой идва на сайта, какво прави и къде се спъва.
            </p>
          </div>
          <div className="flex rounded-lg border border-slate-200 overflow-hidden">
            {RANGES.map((r) => (
              <button
                key={r.key}
                onClick={() => setRange(r.key)}
                className={`px-3 py-1.5 text-[12px] font-semibold transition ${
                  range === r.key ? "bg-[#172554] text-white" : "bg-white text-slate-600 hover:bg-slate-50"
                }`}
              >
                {r.label}
              </button>
            ))}
          </div>
        </div>

        <div className="flex flex-wrap gap-1 border-b border-slate-200">
          {TABS.map((t) => {
            const Icon = t.icon;
            return (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                className={`inline-flex items-center gap-1.5 px-3 py-2 text-[13px] font-semibold border-b-2 -mb-px transition ${
                  tab === t.key
                    ? "border-[#172554] text-[#172554]"
                    : "border-transparent text-slate-500 hover:text-slate-800"
                }`}
              >
                <Icon className="h-4 w-4" />
                {t.label}
              </button>
            );
          })}
        </div>

        {tab === "overview" ? <OverviewTab range={range} onOpenSession={openSessionFromAnywhere} /> : null}
        {tab === "customers" ? (
          <CustomersTab
            selected={selectedUser}
            setSelected={setSelectedUser}
            onOpenSession={openSessionFromAnywhere}
          />
        ) : null}
        {tab === "funnel" ? <FunnelTab range={range} /> : null}
        {tab === "sessions" ? (
          <SessionsTab range={range} openSession={openSession} setOpenSession={setOpenSession} />
        ) : null}
        {tab === "errors" ? <ErrorsTab range={range} onOpenSession={openSessionFromAnywhere} /> : null}
      </div>
    </AdminLayout>
  );
}

export const Route = createFileRoute("/_authenticated/admin/traffic")({
  head: () => ({
    meta: [
      { title: "Админ — Трафик | FIXHEN" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  validateSearch: z.object({
    tab: z.string().optional(),
    session: z.string().optional(),
    user: z.string().optional(),
  }).parse,
  component: TrafficPage,
});
